import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { formatCount, formatPercent } from '@/lib/formatters';
import { lastNDays, DEFAULT_RANGE_DAYS } from '@/lib/dateRange';
import { statsApi } from '@/lib/api/stats';
import { CampaignArtifacts } from '@/components/campaigns/CampaignArtifacts';
import { CampaignFunnel } from '@/components/campaigns/CampaignFunnel';
import { DateRangePicker } from '@/components/charts/DateRangePicker';
import { TimelineChart } from '@/components/charts/TimelineChart';
import { BreakdownList } from '@/components/charts/BreakdownList';
import { HeatmapChart } from '@/components/charts/HeatmapChart';
import type { BreakdownDim, Campaign, DateRange, EventType } from '@/lib/types';

interface CampaignStats {
  campaign: Campaign;
  clicks: number;
  landingViews?: number;
  landingClicks: number;
  installs: number;
  signups: number;
  appOpens?: number;
}

async function fetchStats(id: string): Promise<CampaignStats> {
  const { data } = await api.get<CampaignStats>(`/v1/campaigns/${id}/stats`);
  return data;
}

async function fetchMe(): Promise<import('@/lib/types').Me> {
  const { data } = await api.get<import('@/lib/types').Me>('/v1/me');
  return data;
}

function formatInZone(iso: string, tz: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { timeZone: tz });
  } catch {
    return new Date(iso).toLocaleString();
  }
}

// For redirect campaigns the customer-facing "click" is the direct redirect
// (event_type=click). For landing campaigns no click event is ever recorded —
// the equivalent first-touch is landing_click (button tap on the landing page).
// This helper hides that internal distinction from the dashboard UI.
function firstTouchCount(stats: CampaignStats): number {
  return stats.campaign.type === 'landing' ? stats.landingClicks : stats.clicks;
}

interface BreakdownCardProps {
  campaignId: number;
  dim: BreakdownDim;
  type: EventType;
  range: DateRange;
  title: string;
  description: string;
}

function BreakdownCard({
  campaignId,
  dim,
  type,
  range,
  title,
  description,
}: BreakdownCardProps) {
  const q = useQuery({
    queryKey: queryKeys.campaignBreakdown(campaignId, dim, type, range),
    queryFn: () => statsApi.campaignBreakdown(campaignId, dim, type, range),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {q.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-5/6" />
            <Skeleton className="h-6 w-2/3" />
          </div>
        )}
        {q.isError && (
          <p className="text-sm text-destructive">Could not load.</p>
        )}
        {q.data && <BreakdownList data={q.data} dim={dim} />}
      </CardContent>
    </Card>
  );
}

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const campaignId = Number(id);
  const [range, setRange] = useState<DateRange>(() => lastNDays(DEFAULT_RANGE_DAYS));

  const stats = useQuery({
    queryKey: queryKeys.campaignStats(id ?? ''),
    queryFn: () => fetchStats(id!),
    enabled: !!id,
  });

  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: fetchMe,
    staleTime: 5 * 60_000,
  });
  const tz = me.data?.timezone ?? 'UTC';

  const iosMatchRate = useQuery({
    queryKey: queryKeys.campaignIosMatchRate(campaignId, range),
    queryFn: () => statsApi.campaignIosMatchRate(campaignId, range),
    enabled: !!campaignId,
  });

  const matchedUsers = useQuery({
    queryKey: queryKeys.campaignMatchedUsers(campaignId, 50, 0),
    queryFn: () => statsApi.campaignMatchedUsers(campaignId, 50, 0),
    enabled: !!campaignId,
  });

  const timeline = useQuery({
    queryKey: queryKeys.campaignTimeline(campaignId, range),
    queryFn: () => statsApi.campaignTimeline(campaignId, range),
    enabled: !!campaignId,
  });

  const heatmap = useQuery({
    queryKey: queryKeys.campaignHeatmap(campaignId, 'click', range),
    queryFn: () => statsApi.campaignHeatmap(campaignId, 'click', range),
    enabled: !!campaignId,
  });

  return (
    <div className="space-y-6">
      <Button asChild variant="ghost" size="sm">
        <Link to="/campaigns">
          <ArrowLeft />
          Back to campaigns
        </Link>
      </Button>

      {stats.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
      )}
      {stats.isError && (
        <p className="text-sm text-destructive">Could not load campaign stats.</p>
      )}
      {stats.data && (
        <>
          <header className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold">{stats.data.campaign.name}</h1>
              <p className="text-sm text-muted-foreground">
                Code <span className="font-mono">{stats.data.campaign.code}</span> ·{' '}
                {stats.data.campaign.type} · {stats.data.campaign.source}
              </p>
            </div>
            <DateRangePicker value={range} onChange={setRange} />
          </header>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardDescription>Clicks (all-time)</CardDescription>
                <CardTitle className="text-3xl tabular-nums">
                  {formatCount(firstTouchCount(stats.data))}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader>
                <CardDescription>Installs (all-time)</CardDescription>
                <CardTitle className="text-3xl tabular-nums">
                  {formatCount(stats.data.installs)}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader>
                <CardDescription>Conversion</CardDescription>
                <CardTitle className="text-3xl tabular-nums">
                  {formatPercent(stats.data.installs, firstTouchCount(stats.data))}
                </CardTitle>
              </CardHeader>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Share this campaign</CardTitle>
              <CardDescription>
                Tracking URL, short URL, QR code, and ready-to-paste snippets.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CampaignArtifacts campaign={stats.data.campaign} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Activity over time</CardTitle>
              <CardDescription>
                Daily clicks, landing clicks, installs and signups.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {timeline.isLoading && <Skeleton className="h-[280px] w-full" />}
              {timeline.isError && (
                <p className="text-sm text-destructive">Could not load timeline.</p>
              )}
              {timeline.data && (
                <TimelineChart data={timeline.data} />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Funnel</CardTitle>
              <CardDescription>
                Clicks → Installs → Signups → First activity (all-time).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CampaignFunnel
                clicks={firstTouchCount(stats.data)}
                installs={stats.data.installs}
                signups={stats.data.signups}
                appOpens={stats.data.appOpens}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>iOS match rate</CardTitle>
              <CardDescription>
                Matched iOS installs ÷ iOS clicks in this range. Expect 50–70%
                per spec; a drop below that band suggests IP/NAT matching is
                broken on the visitor network.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {iosMatchRate.isLoading && <Skeleton className="h-10 w-40" />}
              {iosMatchRate.isError && (
                <p className="text-sm text-destructive">Could not load.</p>
              )}
              {iosMatchRate.data && (
                <div className="flex items-baseline gap-6">
                  <div className="text-3xl tabular-nums">
                    {iosMatchRate.data.rate === null
                      ? '—'
                      : `${(iosMatchRate.data.rate * 100).toFixed(1)}%`}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {formatCount(iosMatchRate.data.iosInstalls)} installs from{' '}
                    {formatCount(iosMatchRate.data.iosClicks)} iOS clicks
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Matched users</CardTitle>
              <CardDescription>
                Recently attributed installs for this campaign — newest first.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {matchedUsers.isLoading && (
                <div className="space-y-2">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-3/4" />
                </div>
              )}
              {matchedUsers.isError && (
                <p className="text-sm text-destructive">Could not load.</p>
              )}
              {matchedUsers.data && matchedUsers.data.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No matched users yet.
                </p>
              )}
              {matchedUsers.data && matchedUsers.data.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground">
                      <tr className="text-left">
                        <th className="py-2 pr-4">User ID</th>
                        <th className="py-2 pr-4">Platform</th>
                        <th className="py-2 pr-4">Ref</th>
                        <th className="py-2 pr-4">Matched</th>
                        <th className="py-2">Clicked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matchedUsers.data.map((u) => (
                        <tr key={`${u.userId}-${u.matchedAt}`} className="border-t">
                          <td className="py-2 pr-4 font-mono">{u.userId}</td>
                          <td className="py-2 pr-4">{u.platform}</td>
                          <td className="py-2 pr-4 text-muted-foreground">
                            {u.ref ?? '—'}
                          </td>
                          <td className="py-2 pr-4 tabular-nums text-muted-foreground">
                            {formatInZone(u.matchedAt, tz)}
                          </td>
                          <td className="py-2 tabular-nums text-muted-foreground">
                            {formatInZone(u.clickedAt, tz)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <BreakdownCard
              campaignId={campaignId}
              dim="target_platform"
              type="click"
              range={range}
              title="Store destination"
              description="Where clicks pointed — iOS, Android or web."
            />
            <BreakdownCard
              campaignId={campaignId}
              dim="country"
              type="click"
              range={range}
              title="Country"
              description="Click origin by ISO country code."
            />
            <BreakdownCard
              campaignId={campaignId}
              dim="os"
              type="click"
              range={range}
              title="OS"
              description="Visitor operating system parsed from User-Agent."
            />
            <BreakdownCard
              campaignId={campaignId}
              dim="browser"
              type="click"
              range={range}
              title="Browser"
              description="Visitor browser at click time."
            />
            <BreakdownCard
              campaignId={campaignId}
              dim="device_class"
              type="click"
              range={range}
              title="Device class"
              description="Desktop / phone / tablet split."
            />
            <BreakdownCard
              campaignId={campaignId}
              dim="webview_app"
              type="click"
              range={range}
              title="In-app browsers"
              description="Traffic coming from inside Instagram / TikTok / etc."
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>When clicks come in</CardTitle>
              <CardDescription>
                Day-of-week × hour-of-day heatmap (UTC).
              </CardDescription>
            </CardHeader>
            <CardContent>
              {heatmap.isLoading && <Skeleton className="h-[240px] w-full" />}
              {heatmap.isError && (
                <p className="text-sm text-destructive">Could not load heatmap.</p>
              )}
              {heatmap.data && <HeatmapChart data={heatmap.data} />}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
